import React from 'react'
import styled, {css} from 'styled-components'

import convertLayoutProps from '../../utils/convertLayoutProps'
import FocusTrap from '../focus-trap/FocusTrap'
import {arrowLinkVariants, colors, resetStyles, sizes, variants} from './Link.styles'
import {LinkProps} from './types'

const isAbsoluteLink = (href: string): boolean => href.indexOf('http') === 0

const LinkText = styled.div`
  display: inherit;
`

const LinkArrow = styled.span`
  // Flipping arrow to left ← for RTL languages
  [dir='rtl'] &&:after {
    content: ' \\2190';
  }
  [dir='ltr'] &&:after {
    content: ' \\2192';
  }
`

const BaseLink = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {
    href,
    children,
    target = href && isAbsoluteLink(href) ? '_blank' : undefined,
    withArrow = false,
    className,
    ...restProps
  } = props

  let linkContents = children

  if (withArrow) {
    linkContents = (
      <>
        <LinkText className="link-text">{children}</LinkText>
        <LinkArrow className="link-arrow" aria-hidden="true" />
      </>
    )
  }

  return (
    <a
      ref={ref}
      target={target}
      href={href}
      className={`${className} js__event-link`} // we still need this class to support legacy code
      {...restProps}
    >
      <FocusTrap as="span">{linkContents}</FocusTrap>
    </a>
  )
})

BaseLink.displayName = 'BaseLink'

/**
 * If you need Link that supports styling according to our design tokens
 */
const Link = styled(BaseLink)<LinkProps>`
  ${props => props.theme.normalizeStyles(resetStyles)}

  ${({theme, $variant, withArrow}) =>
    $variant &&
    (withArrow
      ? theme.expandVariants($variant, arrowLinkVariants)
      : theme.expandVariants($variant, variants))}

  ${({theme, $size}) => $size && theme.expandVariants($size, sizes)}
  ${({theme, $color}) => $color && theme.expandVariants($color, colors)}

  ${({theme, $styles, ...props}) =>
    theme.normalizeStyles({...convertLayoutProps(props), ...$styles})};

  ${({$nowrap}) =>
    $nowrap &&
    css`
      white-space: nowrap;
    `}
`

Link.displayName = 'Link'

export default Link
